/**
 * Extension of Time (Section 259(2), M32015) mapper.
 * DCR + Notice 가 값을 공유 → 병합 payload 하나를 양 템플릿에 전달.
 */
import type { SignerEntry } from '@/features/AI-documents/types/docGen.types';
import { formatMyAddressLine } from '@/shared/utils/malaysianAddress';
import { addMonths, formatYmd } from '@/features/Entity_my/utils/myEntityDueDateCalculator';
import { typeOf } from '../components/extensionOfTime/extensionOfTimeFields';

export interface ExtensionOfTimeState {
  values: Record<string, string>;
  flags: Record<string, boolean>;
  choices: Record<string, string>;
  /** [[Signers]] — varKey: 'dcr' | 'notice' */
  signers: Record<string, SignerEntry[]>;
}

export function emptyExtensionOfTimeState(): ExtensionOfTimeState {
  return {
    values: {},
    flags: {
      is_multiple_director_company: false,
      include_circulation: true,
    },
    choices: {
      resolution_basis: 'constitution',
      company_type: 'private',
    },
    signers: {},
  };
}

/** Entity COMPANY_TYPE (M03 코드 / CODE_NAME) → private | public */
export function resolveEotCompanyType(codeOrName: unknown, nameFallback?: unknown): 'private' | 'public' {
  const code = String(codeOrName || '').trim().toUpperCase();
  const name = String(nameFallback || codeOrName || '').trim();
  if (code === 'M03001') return 'public';
  if (code === 'M03002') return 'private';
  if (/public/i.test(name) || (/\b(berhad|bhd)\b/i.test(name) && !/\b(sdn|sendirian)\b/i.test(name))) {
    return 'public';
  }
  return 'private';
}

/** profile / list 날짜 → ISO YYYY-MM-DD */
export function normalizeEotDateToIso(raw: unknown): string {
  if (!raw) return '';
  if (raw instanceof Date && !Number.isNaN(raw.getTime())) {
    return `${raw.getFullYear()}-${String(raw.getMonth() + 1).padStart(2, '0')}-${String(raw.getDate()).padStart(2, '0')}`;
  }
  const s = String(raw).trim();
  if (!s) return '';
  let m = s.match(/^(\d{4})-(\d{1,2})-(\d{1,2})/);
  if (m) return `${m[1]}-${m[2].padStart(2, '0')}-${m[3].padStart(2, '0')}`;
  m = s.match(/^(\d{1,2})[/-](\d{1,2})[/-](\d{4})$/);
  if (m) {
    return `${m[3]}-${m[2].padStart(2, '0')}-${m[1].padStart(2, '0')}`;
  }
  m = s.match(/^(\d{1,2})\s+([A-Za-z]+)$/);
  if (m) {
    const d = new Date(`${m[2]} ${m[1]}, ${new Date().getFullYear()}`);
    if (!Number.isNaN(d.getTime())) {
      return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
    }
  }
  const d = new Date(s);
  if (!Number.isNaN(d.getTime())) {
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
  }
  return '';
}

/** ISO → 문서 표시용 DD/MM/YYYY */
export function formatEotDateDisplay(raw: unknown): string {
  const iso = normalizeEotDateToIso(raw);
  if (!iso) return '';
  const [y, mo, d] = iso.split('-');
  return `${d}/${mo}/${y}`;
}

/** Private Notice: Circulation deadline = FYE + 6 months */
export function computeCirculationDeadlineFromFye(fyeRaw: unknown): string {
  const iso = normalizeEotDateToIso(fyeRaw);
  if (!iso) return '';
  const next = addMonths(iso, 6);
  const ymd = formatYmd(next);
  return ymd ? formatEotDateDisplay(ymd) : '';
}

/** ARTICLE_NO 전체 텍스트 → resolution_basis + clause 번호 */
export function applyEotRegisteredClause(regClause: string): {
  basis: 'constitution' | 'articles' | 'third_schedule';
  clauseNo: string;
} | null {
  const s = String(regClause || '').trim();
  if (!s) return null;
  if (/third\s*schedule/i.test(s)) {
    return { basis: 'third_schedule', clauseNo: '' };
  }
  const m1 = s.match(/(?:Article|Clause)\s+([\w()]+)/i);
  const clauseNo = m1
    ? m1[1]
    : (!/article|clause|constitution|association/i.test(s)
      ? s
      : (s.match(/\b(\d+[\w()]*)/)?.[1] || ''));
  if (/articles?\s+of\s+association/i.test(s) || /\barticles?\b/i.test(s)) {
    return { basis: 'articles', clauseNo };
  }
  return { basis: 'constitution', clauseNo };
}

/** 조건 토글 → 템플릿 [[if X]] 불리언 */
export function expandFlags(state: ExtensionOfTimeState): Record<string, boolean> {
  const f = state.flags || {};
  const c = state.choices || {};
  const basis = c.resolution_basis || 'constitution';
  const companyType = c.company_type || 'private';
  return {
    is_multiple_director_company: !!f.is_multiple_director_company,
    is_sole_director_company: !f.is_multiple_director_company,
    include_circulation: f.include_circulation !== false,
    resolution_basis_constitution: basis === 'constitution',
    resolution_basis_articles: basis === 'articles',
    resolution_basis_third_schedule: basis === 'third_schedule',
    is_private_company: companyType === 'private',
    is_public_company: companyType === 'public',
  };
}

export function buildExtensionOfTimeVariables(state: ExtensionOfTimeState): Record<string, any> {
  const out: Record<string, any> = {};
  for (const [k, v] of Object.entries(state.values || {})) {
    const raw = v ?? '';
    out[k] = typeOf(k) === 'address' ? formatMyAddressLine(String(raw)) : raw;
  }
  Object.assign(out, expandFlags(state));

  const basis = state.choices?.resolution_basis || 'constitution';
  const clause = String(state.values?.constitution_clause_number || '').trim();
  out.resolution_basis_clause =
    basis === 'third_schedule'
      ? 'Paragraph 15 of the Third Schedule of the Companies Act 2016'
      : basis === 'articles'
        ? (clause
          ? `Article ${clause} of the Articles of Association`
          : 'the Articles of Association')
        : (clause
          ? `Clause ${clause} of the Company's Constitution`
          : "the Company's Constitution");

  return out;
}
